<template>
  <button
    type="button"
    class="deck-card-row"
    :class="[categoryClass, {
      active,
      full: atLimit,
      'just-sent': justSent,
      denied: justDenied,
      holding,
      'materials-ready': materialsReady,
    }]"
    :title="rowTitle"
    @mouseenter="$emit('hover')"
    @pointerdown="onPointerDown"
    @click="onClick"
    @contextmenu.prevent="onContextMenu"
  >
    <div class="row-art-wrap">
      <CardArt
        class="row-art"
        :card-id="card.id"
        :foil="foil"
        :alt="display.name"
        intensity="thumb"
      />
      <span
        v-if="pvpLimit != null"
        class="row-pvp-limit"
        :title="t('deck.pvpLimitBadge', { n: pvpLimit })"
        aria-hidden="true"
      >{{ pvpLimit }}</span>
    </div>
    <div class="row-copy">
      <div class="row-name">{{ display.name }}</div>
      <div class="row-meta">
        <span>{{ typeLine }}</span>
        <span v-if="levelLabel" class="row-level">
          <span class="row-star" aria-hidden="true">&#9733;</span>
          {{ levelLabel }}
        </span>
      </div>
      <div v-if="showStats" class="row-stats">
        <span class="stat atk">{{ card.atk }}</span>
        <span class="stat-sep">/</span>
        <span class="stat def">{{ card.def }}</span>
      </div>
    </div>
    <span class="row-qty">{{ inDeck }}/{{ owned }}</span>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { asCardFoil, type CardFoilId } from '@shared/index'
import { useSiteStore } from '../stores/site'
import { useHoldToFill } from '../composables/useHoldToFill'
import { localizeCard } from '../i18n/localizeCard'
import { useI18n } from '../i18n/t'
import CardArt from './CardArt.vue'

const props = defineProps<{
  card: any
  inDeck: number
  owned: number
  active?: boolean
  foil?: CardFoilId | string | null
  justSent?: boolean
  justDenied?: boolean
  materialsReady?: boolean
}>()

const emit = defineEmits<{
  hover: []
  pick: [src: HTMLElement]
  fill: [src: HTMLElement]
  contextmenu: [event: MouseEvent]
}>()

const { t } = useI18n()
const { holding, begin, consumeClick, consumeContext } = useHoldToFill((el) => {
  emit('fill', el)
})

function onPointerDown(event: PointerEvent): void {
  emit('hover')
  begin(event)
}

function onClick(event: MouseEvent): void {
  if (consumeClick()) return
  const el = event.currentTarget as HTMLElement
  if (el) emit('pick', el)
}

function onContextMenu(event: MouseEvent): void {
  const action = consumeContext()
  if (action === 'fill' || action === 'skip') return
  emit('contextmenu', event)
}

const foil = computed(() => asCardFoil(props.foil ?? props.card?.foil))

const site = useSiteStore()
const pvpLimit = computed(() => {
  const id = String(props.card?.id ?? '')
  const max = site.houseLimits[id]
  return max === undefined ? null : max
})

const display = computed(() => localizeCard(props.card || {}))

const rowTitle = computed(
  () => `${display.value.name} — ${t('deck.holdToAddThree')}`,
)

const category = computed((): 'monster' | 'fusion' | 'spell' | 'trap' | 'other' => {
  const card = props.card
  if (Array.isArray(card?.typeline)) {
    if (card.typeline.includes('Fusion')) return 'fusion'
    if (card.typeline.includes('Monster')) return 'monster'
    if (card.typeline.includes('Spell')) return 'spell'
    if (card.typeline.includes('Trap')) return 'trap'
  }
  const type = String(card?.type || '').toLowerCase()
  if (type.includes('fusion')) return 'fusion'
  if (type.includes('monster')) return 'monster'
  if (type.includes('spell')) return 'spell'
  if (type.includes('trap')) return 'trap'
  return 'other'
})

const categoryClass = computed(() => `is-${category.value}`)

const typeLine = computed(() => {
  const shown = display.value
  if (category.value === 'monster' || category.value === 'fusion') {
    return [shown.attribute, shown.race].filter(Boolean).join(' / ')
  }
  return [shown.type, shown.race].filter(Boolean).join(' / ')
})

const levelLabel = computed(() => {
  if (
    (category.value !== 'monster' && category.value !== 'fusion')
    || !props.card?.level
  ) return ''
  return String(props.card.level)
})

const showStats = computed(() =>
  (category.value === 'monster' || category.value === 'fusion')
  && props.card?.atk != null
  && props.card?.def != null,
)

const atLimit = computed(() => props.inDeck >= props.owned)
</script>

<style scoped>
.deck-card-row {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-width: 0;
  height: 100%;
  padding: 8px 12px 8px 10px;
  text-align: left;
  background: var(--md-sys-color-surface-container-high, #2a2a2e);
  border: 0;
  border-radius: 12px;
  border-left: 3px solid transparent;
  color: inherit;
  cursor: pointer;
  overflow: hidden;
  font-family: Roboto, system-ui, sans-serif;
  user-select: none;
  touch-action: pan-y;
  -webkit-touch-callout: none;
}

.deck-card-row::after {
  content: '';
  position: absolute;
  inset: 0;
  background: var(--md-sys-color-on-surface, #e5e1e6);
  opacity: 0;
  pointer-events: none;
  transition: opacity 200ms cubic-bezier(0.2, 0, 0, 1);
}

.deck-card-row.is-monster {
  border-left-color: #e2a06a;
}

.deck-card-row.is-fusion {
  border-left-color: #ce93d8;
}

.deck-card-row.is-spell {
  border-left-color: #81c784;
}

.deck-card-row.is-trap {
  border-left-color: #ce93d8;
}

.deck-card-row:hover::after,
.deck-card-row.active::after {
  opacity: 0.08;
}

.deck-card-row.active {
  background: var(--md-sys-color-primary-container, #6e5c1a);
  color: var(--md-sys-color-on-primary-container, #f5f5f5);
}

.deck-card-row.full {
  opacity: 0.55;
}

.deck-card-row.just-sent {
  animation: row-sent 420ms cubic-bezier(0.2, 0, 0, 1);
}

.deck-card-row.denied {
  animation: row-deny 280ms ease;
}

.deck-card-row.holding::before {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  z-index: 2;
  height: 3px;
  width: 100%;
  transform-origin: left center;
  background: var(--md-sys-color-primary, #e8c547);
  animation: hold-fill 450ms linear forwards;
  pointer-events: none;
}

@keyframes hold-fill {
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
}

@keyframes row-sent {
  0% {
    box-shadow: inset 0 0 0 0 color-mix(in srgb, var(--md-sys-color-primary) 0%, transparent);
  }
  35% {
    box-shadow: inset 0 0 0 2px var(--md-sys-color-primary, #e8c547);
  }
  100% {
    box-shadow: inset 0 0 0 0 color-mix(in srgb, var(--md-sys-color-primary) 0%, transparent);
  }
}

@keyframes row-deny {
  0%,
  100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-4px);
  }
  75% {
    transform: translateX(4px);
  }
}

.row-art-wrap {
  position: relative;
  z-index: 1;
  flex-shrink: 0;
}

.row-art {
  position: relative;
  z-index: 1;
  width: 42px;
  height: 61px;
  flex-shrink: 0;
  border-radius: 4px;
  background: var(--md-sys-color-surface, #131316);
}

.row-pvp-limit {
  position: absolute;
  top: -4px;
  left: -4px;
  z-index: 2;
  min-width: 16px;
  padding: 1px 4px;
  border-radius: 999px;
  background: #b3261e;
  color: #fff;
  font-size: 9px;
  font-weight: 800;
  line-height: 1.35;
  text-align: center;
  font-variant-numeric: tabular-nums;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.45);
}

.row-copy {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  position: relative;
  z-index: 1;
}

.row-name {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: var(--md-sys-color-on-surface, #e5e1e6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.deck-card-row.active .row-name {
  color: var(--md-sys-color-on-primary-container, #f5f5f5);
}

.deck-card-row.materials-ready:not(.active) .row-name {
  color: light-dark(#166534, #4ade80);
}

.row-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 12px;
  line-height: 16px;
  color: var(--md-sys-color-on-surface-variant, #c6c5d0);
}

.row-level {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.row-star {
  color: var(--md-sys-color-primary, #e8c547);
  font-size: 11px;
}

.row-stats {
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.4px;
}

.stat.atk {
  color: #f2b8b5;
}

.stat-sep {
  color: var(--md-sys-color-outline, #90909a);
  margin: 0 3px;
}

.stat.def {
  color: #8ab4f8;
}

.row-qty {
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  min-width: 32px;
  height: 24px;
  padding: 0 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: var(--md-sys-color-primary, #e8c547);
  color: var(--md-sys-color-on-primary, #1c1b1f);
  font-size: 11px;
  font-weight: 500;
}

.just-sent .row-qty {
  animation: qty-bump 420ms cubic-bezier(0.2, 0, 0, 1);
}

@keyframes qty-bump {
  0% {
    transform: scale(1);
  }
  35% {
    transform: scale(1.22);
  }
  100% {
    transform: scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .deck-card-row.just-sent,
  .deck-card-row.denied,
  .just-sent .row-qty,
  .deck-card-row.holding::before {
    animation: none;
  }
}
</style>
